import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Search, Sparkles, X, Layers } from "lucide-react";
import api from "@/lib/apiClient";
import SEO from "@/components/SEO";
import { ISSUE_ICONS, searchIssues } from "@/lib/issues";
import IssueCard from "@/components/issues/IssueCard";
import IssueViewer from "@/components/issues/IssueViewer";

const kebab = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Hero({ q, setQ, count }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return (
    <section ref={ref} className="relative overflow-hidden border-b border-npst-line paper-texture" data-no-reveal data-testid="issues-page-hero">
      <motion.div style={{ y }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-24 -top-10 h-[420px] w-[420px] rounded-full bg-npst-goldLight opacity-90 blur-[90px]" />
        <div className="absolute -left-20 top-40 h-[360px] w-[360px] rounded-full bg-slate-200 opacity-50 blur-[100px]" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative mx-auto max-w-7xl px-4 pb-14 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-3 label-eyebrow text-npst-gold">
          <span className="h-px w-8 bg-npst-gold" /> Digital exhibition · {count} issues
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
          className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-npst-ink" data-testid="issues-page-title">
          Under the <span className="text-npst-navy">Lens</span>
        </motion.h1>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="relative mt-10 max-w-2xl">
          <Search size={18} className="pointer-events-none absolute left-5 top-1/2 z-10 -translate-y-1/2 text-npst-gold" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask anything — e.g. “toilets”, “police custody”"
            data-testid="issues-search-input" aria-label="Search issues"
            className="h-14 w-full rounded-full border border-npst-line bg-white pl-12 pr-28 text-sm text-npst-ink shadow-[0_8px_30px_-18px_rgba(15,41,74,0.35)] placeholder:text-npst-muted focus:border-npst-gold focus:outline-none" />
          {q ? <button onClick={() => setQ("")} data-testid="issues-search-clear" aria-label="Clear search" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-npst-muted hover:text-npst-navy"><X size={16} /></button>
            : <span className="absolute right-5 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] text-npst-gold"><Sparkles size={11} /> AI index</span>}
        </motion.div>
      </motion.div>
    </section>
  );
}

function Chips({ items, value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {["All", ...items].map((c) => {
        const on = value === c || (c === "All" && !value);
        return (
          <button key={c} onClick={() => onChange(c === "All" ? "" : c)} data-testid={`${prefix}-filter-chip-${kebab(c)}`}
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-medium transition-colors ${on ? "border-npst-navy bg-npst-navy text-white" : "border-npst-line bg-white text-npst-inkSoft hover:border-npst-goldBorder hover:text-npst-navy"}`}>{c}</button>
        );
      })}
    </div>
  );
}

export default function Issues() {
  const [issues, setIssues] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [cat, setCat] = useState(params.get("category") || "");
  const [status, setStatus] = useState("");
  const openSlug = params.get("issue");
  useEffect(() => { api.get("/issues").then(({ data }) => setIssues(data)).catch(() => setIssues([])); }, []);
  const cats = useMemo(() => [...new Set((issues || []).map((i) => i.category))], [issues]);
  const statuses = useMemo(() => [...new Set((issues || []).map((i) => i.status))], [issues]);
  const shown = useMemo(() => searchIssues(issues || [], q).filter((i) => (!cat || i.category === cat) && (!status || i.status === status)), [issues, q, cat, status]);
  const open = (i) => setParams((p) => { const n = new URLSearchParams(p); n.set("issue", i.slug); return n; });
  const close = useCallback(() => setParams((p) => { const n = new URLSearchParams(p); n.delete("issue"); return n; }), [setParams]);
  const preview = (issues || []).find((i) => i.slug === openSlug);
  return (
    <div className="min-h-screen bg-npst-bg" data-testid="issues-page">
      <SEO route="/issues" title="Under the Lens | NPST India" description="Public-interest issues currently under the lens of the National Public Service Trust." canonical={`${window.location.origin}/issues`} />
      <Hero q={q} setQ={setQ} count={issues?.length || 0} />
      <div className="relative z-10 border-b border-npst-line bg-npst-bgAlt/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <Chips items={cats} value={cat} onChange={setCat} prefix="category" />
          {statuses.length > 1 && <Chips items={statuses} value={status} onChange={setStatus} prefix="status" />}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {issues === null && <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`mb-6 skeleton rounded-xl ${i % 2 ? "h-80" : "h-[400px]"}`} />)}</div>}
        {issues && shown.length === 0 && (
          <div className="flex flex-col items-center py-24 text-center" data-testid="empty-issues-state">
            <Layers size={44} strokeWidth={1} className="text-npst-gold/60" />
            <div className="mt-5 font-serif text-2xl text-npst-ink">{issues.length ? "No matching issues" : "Exhibition being curated"}</div>
            {q && <button onClick={() => { setQ(""); setCat(""); setStatus(""); }} className="mt-5 text-xs uppercase tracking-[0.18em] text-npst-gold hover:text-npst-navy" data-testid="issues-reset-filters">Reset</button>}
          </div>
        )}
        <motion.div layout className="columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="issues-gallery">
          <AnimatePresence>{shown.map((i, idx) => <IssueCard key={i.id} issue={i} index={idx} onOpen={open} />)}</AnimatePresence>
        </motion.div>
        {issues?.length > 0 && (
          <div className="mt-10 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-npst-muted" data-testid="issues-ai-note">
            <Sparkles size={12} className="text-npst-gold" /> Images are AI-generated illustrations · Records are verified public documents
          </div>
        )}
      </div>
      <AnimatePresence>{openSlug && <IssueViewer key={openSlug} slug={openSlug} preview={preview} onClose={close} />}</AnimatePresence>
    </div>
  );
}

export { ISSUE_ICONS };
